import type { CenturySystem_CenturyPvUpload } from '@comped/fe-shared/dist/dbs';

import type { EmptyObject, TransformDateProperties } from '../commons';
import type { LogItemInfoRaw, PasswordMigratorProgressInfoRaw, ProxyType } from './century-shared';

export type CenturyViewDocRequest = {
    proxyType?: ProxyType;
    documentId: string | number;
    partId?: string | number;
    download?: 'true';
};

export type CenturyViewDocResponse = {
    Url?: string;
};

export type CenturyViewStdMetadataRequest = CenturyViewDocRequest;

export type CenturyViewStdMetadataResponse = {
    //base64 enoded
    stdMetadata?: string;
};

export type CenturyExportProfileRequest = {
    group?: string | number | null;
    owner?: string | null;
    profile: string;
    inline?: boolean;
};

export type CenturyExportProfileResponse = string;

export const CONSERV_INDEX_MODE = {
    require_mdc: 1,
    require_valid: 2,
    use_autocreated: 3,
    autocreate: 4,
} as const;
export type ConservIndexMode = keyof typeof CONSERV_INDEX_MODE;

export const USE_VOLUME_STORAGE_TYPE = {
    UNPROTECTED: 0,
    PROTECTED: 1,
    DEFAULT: 2,
    FLAT: 3,
    SEVENZIP_FRAGMENTED: 4,
    SEVENZIP_FRAGMENTED_PROTECTED: 5,
    SEVENZIP: 6,
    SEVENZIP_PROTECTED: 7,
} as const;
export type VolumeStorageType = keyof typeof USE_VOLUME_STORAGE_TYPE;

export type CenturyManageVolumeRequest = {
    proxyType?: ProxyType;
    volumeId: string | number;
} & ({
    action: 'validateVolume'
    | 'signVolume'
    | 'closeVolume'
    | 'deleteVolumeToTrash'
    | 'restoreDeletedVolume'
    | 'ensureVolumeIndexed'
    | 'deleteVolumeMetadataIndex';
} | {
    action: 'migrateVolumeStorage';
    nextVolumeStorageType: VolumeStorageType;
} | {
    action: 'executeConservIndexVolume';
    conservIndexMode: ConservIndexMode;
} | {
    action: 'createPddOrArchive';
    description: string;
    volumePddPassword?: string;
});

export type CenturyManageVolumeResponse = EmptyObject;

export type CenturyGetVolumeLogRequest = {
    proxyType?: ProxyType;
    volumeId: string | number;
    fromItemId: string | number;
    count?: string | number;
};

export type CenturyGetVolumeLogResponse = {
    volumeLog: (TransformDateProperties<LogItemInfoRaw>)[];
};

export type CenturyUploadDocGetUploadsResponse =
Pick<CenturySystem_CenturyPvUpload, 'group' | 'creator_owner' | 'profile' | 'queue' | 'queue_uri'> & {
    document_id: string | number;
    owner: string;
};

export type PdvMetadata = Record<string, string>;

export interface CenturyApi {
    'CenturyViewDoc': {
        request: CenturyViewDocRequest;
        response: CenturyViewDocResponse;
    };
    'CenturyViewStdMetadata': {
        request: CenturyViewStdMetadataRequest;
        response: CenturyViewStdMetadataResponse;
    };
    'CenturyManageVolume': {
        request: CenturyManageVolumeRequest;
        response: CenturyManageVolumeResponse;
    };
    'CenturyExportProfile': {
        request: CenturyExportProfileRequest;
        response: CenturyExportProfileResponse;
    };
    'CenturyGetVolumeLog': {
        request: CenturyGetVolumeLogRequest;
        response: CenturyGetVolumeLogResponse;
    };
    'CenturyMasterLoginManage': {
        request: {
            masterPassword?: string;
            masterPasswordLogout?: string;
            oldMasterPassword?: string;
            newMasterPassword?: string;
        };
        response: {
            status: string | boolean;
        };
    };
    'CenturyMasterLoginMigrationProgress': {
        request: EmptyObject;
        response: {
            progress: TransformDateProperties<PasswordMigratorProgressInfoRaw>;
        };
    };
    'CenturyUploadDocGetUploads': {
        request: EmptyObject;
        response: {
            uploads: CenturyUploadDocGetUploadsResponse[];
        };
    };
    'CenturyUploadNewUpload': {
        request: {
            group?: string | number | null;
            owner?: string | null;
            profile: string;
            queue: string;
            metadata: {
                name: string;
                schema: string;
                meta: Record<string, string>;
            };
        };
        response: {
            document_id: string | number;
        };
    };
    'CenturyUploadSubmitUpload': {
        request: {
            document_id: string | number;
            pv_meta: PdvMetadata;
        };
        response: {
            size_of_files: number | string;
            count_of_parts: number | string;
            Url?: string;
        };
    };
}
